import { useDisclosure, useSelection } from '#shared'
import { computed, reactive, unref, type Ref } from 'vue'
import type { Emoji } from '#features/emoji-selection'

export function useEmojiController(inputEl: Ref<HTMLTextAreaElement | null>) {
  const emojiSelectDisclosure = useDisclosure()

  const selection = useSelection(inputEl)

  const maxTextLength = import.meta.env.VITE_MAX_MESSAGE_SYMBOLS

  const onEmojiSelect = (emoji: Emoji) => {
    const inputElVal = inputEl.value
    if (!inputElVal) return

    const message = inputElVal.value

    const start = selection.value.start ?? message.length
    const end = selection.value.end ?? message.length

    const newMessageValue = message.slice(0, start) + emoji.char + message.slice(end)
    if (newMessageValue.length > maxTextLength) return

    inputElVal.value = newMessageValue

    inputElVal.dispatchEvent(new Event('input', { bubbles: true }))

    inputElVal.focus()
    inputElVal.setSelectionRange(start + emoji.char.length, start + emoji.char.length)
  }

  const restoreInputFocus = () => {
    const inputElVal = unref(inputEl)
    if (!inputElVal) return

    const message = inputElVal.value
    const start = selection.value.start ?? message.length
    const end = selection.value.end ?? message.length

    inputElVal.focus()
    inputElVal.setSelectionRange(start, end)
  }

  const onEmojiSelectClose = () => {
    emojiSelectDisclosure.close()
    restoreInputFocus()
  }

  const onEmojiSelectToggle = () => {
    emojiSelectDisclosure.toggle()
    restoreInputFocus()

    setTimeout(() => {
      restoreInputFocus()
    })
  }

  const isEmojiSelectOpen = computed(() => emojiSelectDisclosure.isOpen)

  return reactive({
    emojiSelectDisclosure,
    onEmojiSelect,
    onEmojiSelectClose,
    onEmojiSelectToggle,
    isEmojiSelectOpen,
  })
}

export type EmojiController = ReturnType<typeof useEmojiController>
